import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { RESET, atomWithReset } from 'jotai/utils';

import { remoteConfigAtom } from 'src/stores/auth/atoms';
import {
  DLC_MODEL_TYPES,
  invalidateAndRefetchModelAtom,
  loadStaticFallbackAtom,
  modelEntryAtoms,
  STATIC_MODEL_IDS,
} from 'src/stores/downloadableContents/modelRegistry';
import { ModelEntry } from 'src/stores/downloadableContents/types';
import type { RemoteConfig } from 'src/types/AzarUser';
import { isObjectKeyTrue } from 'src/utils/common';
import { AntmanResultResponse } from 'src/utils/media/pipes/antman';
import { HumanVisionInferenceResponse } from 'src/utils/media/pipes/humanvision';
import type LidAudioPipe from 'src/utils/media/pipes/lid';
import type { LidInferenceResult } from 'src/utils/media/pipes/lid';

import { GetMLModelConfigsResponse, getMLModelConfigsAPI } from './apis';
import { createFeatureTriggerState, evaluateFeature } from './humanvisionTrigger';
import {
  AntmanFeatureCaptureData,
  BaseMLConfig,
  FeatureTriggerState,
  HvmFeatureCaptureData,
  MLConfigStore,
  MLFeatureConfig,
  MLFeatureType,
  MLInference,
  MLModelConfig,
  MLModelType,
  MLResolvedModelConfig,
} from './types';

const AntmanMediaStreamPromise = import('src/utils/media/pipes/antman');
type AntmanMediaStreamInstance = InstanceType<Awaited<typeof AntmanMediaStreamPromise>['default']>;

// =============================================================================
// Antman — enable / suspend
// =============================================================================

export const antmanEnableAtom = atom<boolean>(true);

// antman이 동작해야하나 성능 등의 이슈로 중간에 동작이 중단되었는지 여부
export const antmanSuspendedAtom = atomWithReset(false);

// =============================================================================
// Antman — periodic report (inference queue / counters for match report)
// =============================================================================

export const antmanInferenceReportTimerAtom = atom<ReturnType<typeof setInterval> | null>(null);
export const antmanInferenceReportIndexAtom = atomWithReset(0);
export const antmanInferenceQueueAtom = atomWithReset<MLInference[]>([]);
export const antmanLatencySumAtom = atomWithReset(0);
export const antmanInferenceCountAtom = atomWithReset(0);

// =============================================================================
// Antman — feature capture & last inference snapshot
// =============================================================================

export const antmanResultAtom = atomWithReset<AntmanResultResponse | null>(null);
export const antmanFeaturesCapturedAtom = atomWithReset<{
  [key in MLFeatureType]?: AntmanFeatureCaptureData;
}>({});

// =============================================================================
// Antman — model / feature config (fallback + writable atom)
// =============================================================================

/**
 * 적용하려는 앤트맨의 설정값이나 모델 부재 등 부득이한 경우에 fallback으로 사용하는 모델 정보
 * ver: tflite 2.0.0
 */
const FALLBACK_ANTMAN_MODEL_ID = STATIC_MODEL_IDS[MLModelType.Antman];

const FALLBACK_ANTMAN_FEATURES_CONFIG: MLFeatureConfig[] = [
  {
    featureType: MLFeatureType.MatchBlur,
    featureCondition: [
      {
        modelType: MLModelType.Antman,
        modelIndex: 1,
        criteriaWindow: 1,
        criteriaThreshold: 1,
        threshold: 0.98,
        maxInferencesLimit: 60,
      },
    ],
  },
  {
    featureType: MLFeatureType.MatchAutoReportImage,
    featureCondition: [
      {
        modelType: MLModelType.Antman,
        modelIndex: 1,
        criteriaWindow: 1,
        criteriaThreshold: 1,
        threshold: 0.98,
        maxInferencesLimit: 60,
      },
    ],
  },
];

// =============================================================================
// HVM — enable / suspend & feature capture
// =============================================================================

// hvm이 동작해야하나 성능 등의 이슈로 중간에 동작이 중단되었는지 여부
export const hvmSuspendedAtom = atomWithReset(false);

// =============================================================================
// HVM — periodic report (inference queue / counters for match report)
// =============================================================================

export const hvmInferenceReportTimerAtom = atom<ReturnType<typeof setInterval> | null>(null);
export const hvmInferenceReportIndexAtom = atomWithReset(0);
export const hvmInferenceQueueAtom = atomWithReset<MLInference[]>([]);
export const hvmLatencySumAtom = atomWithReset(0);
export const hvmInferenceCountAtom = atomWithReset(0);

// =============================================================================
// HVM — model / feature config (fallback + writable atoms)
// =============================================================================

const HVM_MODEL_ID = STATIC_MODEL_IDS[MLModelType.HumanVision];
const LID_MODEL_ID = STATIC_MODEL_IDS[MLModelType.LID];

const FALLBACK_HVM_FEATURES_CONFIG: MLFeatureConfig[] = [
  {
    featureType: MLFeatureType.AfterMatchHVReportPopupMinor,
    featureCondition: [
      {
        modelType: MLModelType.HumanVision,
        modelIndex: 7,
        criteriaWindow: 1,
        criteriaThreshold: 1,
        threshold: 0.7534,
        maxInferencesLimit: 60,
      },
    ],
  },
  {
    featureType: MLFeatureType.MatchHVUnderageExistAutoReport,
    featureCondition: [
      {
        modelType: MLModelType.HumanVision,
        modelIndex: 7,
        criteriaWindow: 1,
        criteriaThreshold: 1,
        threshold: 0.8334,
        maxInferencesLimit: 60,
      },
    ],
  },
] as const;

const FALLBACK_ML_MODEL_TYPES = [
  MLModelType.Antman,
  MLModelType.HumanVision,
  MLModelType.LID,
] as const;
type FallbackMLModelType = (typeof FALLBACK_ML_MODEL_TYPES)[number];

function isFallbackMLModelType(modelType: MLModelType): modelType is FallbackMLModelType {
  return FALLBACK_ML_MODEL_TYPES.includes(modelType as FallbackMLModelType);
}

export const FALLBACK_ML_CONFIGS: Record<FallbackMLModelType, MLResolvedModelConfig> = {
  [MLModelType.Antman]: {
    modelType: MLModelType.Antman,
    modelId: FALLBACK_ANTMAN_MODEL_ID,
    modelConfig: {
      modelId: FALLBACK_ANTMAN_MODEL_ID,
      type: MLModelType.Antman,
      intervalMs: 100,
      maxLatencyLimit: 1000,
    },
    featureConfigs: [...FALLBACK_ANTMAN_FEATURES_CONFIG],
    source: 'fallback',
  },
  [MLModelType.HumanVision]: {
    modelType: MLModelType.HumanVision,
    modelId: HVM_MODEL_ID,
    modelConfig: {
      modelId: HVM_MODEL_ID,
      type: MLModelType.HumanVision,
      intervalMs: 2000,
      maxLatencyLimit: 2000,
    },
    featureConfigs: [...FALLBACK_HVM_FEATURES_CONFIG],
    source: 'fallback',
  },
  [MLModelType.LID]: {
    modelType: MLModelType.LID,
    modelId: LID_MODEL_ID,
    // ailab-portal의 LID 모델 설정을 옮겨온 값.
    modelConfig: {
      modelId: LID_MODEL_ID,
      type: MLModelType.LID,
      intervalMs: 7000,
      maxLatencyLimit: 3000,
      maxRun: 10,
    },
    featureConfigs: [],
    source: 'fallback',
  },
};

export const mlConfigStoreAtom = atomWithReset<MLConfigStore>({
  [MLModelType.Antman]: FALLBACK_ML_CONFIGS[MLModelType.Antman],
  [MLModelType.HumanVision]: FALLBACK_ML_CONFIGS[MLModelType.HumanVision],
  [MLModelType.LID]: FALLBACK_ML_CONFIGS[MLModelType.LID],
});

function getResolvedMLConfig(
  store: MLConfigStore,
  modelType: FallbackMLModelType
): MLResolvedModelConfig {
  return store[modelType] ?? FALLBACK_ML_CONFIGS[modelType];
}

export const antmanConfigAtom = atom((get) =>
  getResolvedMLConfig(get(mlConfigStoreAtom), MLModelType.Antman)
);

export const hvmModelConfigAtom = atom<BaseMLConfig>((get) => {
  const { modelId, modelConfig } = getResolvedMLConfig(
    get(mlConfigStoreAtom),
    MLModelType.HumanVision
  );
  return { modelId, modelConfig };
});

export const hvmFeatureConfigsAtom = atom(
  (get) => getResolvedMLConfig(get(mlConfigStoreAtom), MLModelType.HumanVision).featureConfigs
);

export const lidModelConfigAtom = atom<BaseMLConfig | null>((get) => {
  const config = get(mlConfigStoreAtom)[MLModelType.LID];
  if (!config) return null;
  return { modelId: config.modelId, modelConfig: config.modelConfig };
});

export const lidFeatureConfigsAtom = atom(
  (get) => get(mlConfigStoreAtom)[MLModelType.LID]?.featureConfigs ?? []
);

export function findMLFeatureCondition(config: MLResolvedModelConfig, featureType: MLFeatureType) {
  return config.featureConfigs
    .find((featureConfig) => featureConfig.featureType === featureType)
    ?.featureCondition.find((featureCondition) => featureCondition.modelType === config.modelType);
}

export function shouldLoadStaticFallbackModel(entry: ModelEntry, fallbackModelId: string) {
  if (entry.status !== 'ready' || !entry.buffer) return true;
  return entry.modelId !== fallbackModelId;
}

type ReadyModelEntry = ModelEntry & {
  status: 'ready';
  buffer: ArrayBuffer;
  modelId: string;
};

function isReadyModelEntry(entry: ModelEntry): entry is ReadyModelEntry {
  return entry.status === 'ready' && !!entry.buffer && !!entry.modelId;
}

function createModelFeatureTriggerStates(
  featureConfigs: MLFeatureConfig[],
  modelType: MLModelType
) {
  return featureConfigs.flatMap(({ featureType, featureCondition }) =>
    featureCondition
      .filter((fc) => fc.modelType === modelType)
      .map((condition) => createFeatureTriggerState(featureType, condition))
  );
}

function evaluateTriggeredFeatureStates(states: FeatureTriggerState[], result: number[]) {
  const triggeredStates: FeatureTriggerState[] = [];
  const now = Date.now();

  states.forEach((state) => {
    if (evaluateFeature(state, result)) {
      triggeredStates.push(state);
      if (state.triggeredAt === null) {
        state.triggeredAt = now;
      }
    }
  });

  return triggeredStates;
}

// =============================================================================
// HVM — feature capture
// =============================================================================

// Antman antmanFeaturesCapturedAtom과 동일 패턴.
// score 필드로 매치 내 최고 score 이미지를 유지한다 (Web 추가 스펙).
export const hvmFeaturesCapturedAtom = atomWithReset<{
  [key in MLFeatureType]?: HvmFeatureCaptureData;
}>({});

/**
 * RC에 따라 HVM feature config를 필터링한다.
 * Android ChatFragmentKt:1661 — RC off일 때 MatchHVUnderageExistAutoReport feature를 미등록하는 것에 대응.
 */
type MLRemoteConfig =
  | Pick<RemoteConfig, 'humanVisionMobileEnabled' | 'inMatchUnderageWarningFeatureEnabled'>
  | undefined;

function isHumanVisionEnabled(remoteConfig: MLRemoteConfig) {
  return !!remoteConfig && isObjectKeyTrue(remoteConfig, 'humanVisionMobileEnabled');
}

function filterHvmFeaturesByRC(
  features: MLFeatureConfig[],
  remoteConfig: MLRemoteConfig
): MLFeatureConfig[] {
  if (!remoteConfig) return features;
  return features.filter((f) => {
    if (
      f.featureType === MLFeatureType.MatchHVUnderageExistAutoReport &&
      !isObjectKeyTrue(remoteConfig, 'inMatchUnderageWarningFeatureEnabled')
    ) {
      return false;
    }
    return true;
  });
}

export const applyFallbackMLConfigAtom = atom(null, (get, set, modelType: FallbackMLModelType) => {
  const fallbackConfig = FALLBACK_ML_CONFIGS[modelType];
  const featureConfigs =
    modelType === MLModelType.HumanVision
      ? filterHvmFeaturesByRC(fallbackConfig.featureConfigs, get(remoteConfigAtom))
      : fallbackConfig.featureConfigs;

  set(mlConfigStoreAtom, {
    ...get(mlConfigStoreAtom),
    [modelType]: {
      ...fallbackConfig,
      featureConfigs,
    },
  });
});

export const ensureFallbackModelAtom = atom(
  null,
  async (get, set, modelType: FallbackMLModelType) => {
    const fallbackConfig = FALLBACK_ML_CONFIGS[modelType];
    const entry = get(modelEntryAtoms[modelType]);
    if (!shouldLoadStaticFallbackModel(entry, fallbackConfig.modelId)) return;
    await set(loadStaticFallbackAtom, modelType);
  }
);

// =============================================================================
// ML config bootstrap (Antman + HVM API)
// =============================================================================

function resolveModelConfigFromAPI(
  modelType: MLModelType,
  modelId: string,
  response: GetMLModelConfigsResponse
): MLResolvedModelConfig | null {
  const modelConfig = response.models.find((model) => model.modelId === modelId);
  if (!modelConfig) return null;

  const featureConfigs = response.features
    .map((featureConfig) => ({
      ...featureConfig,
      featureCondition: featureConfig.featureCondition.filter(
        (condition) => condition.modelType === modelType
      ),
    }))
    .filter((featureConfig) => featureConfig.featureCondition.length > 0);

  return {
    modelType,
    modelId,
    modelConfig,
    featureConfigs,
    source: 'api',
  };
}

// =============================================================================
// LID — periodic report (inference queue / counters for match report)
// =============================================================================

export const lidInferenceReportTimerAtom = atom<ReturnType<typeof setInterval> | null>(null);
export const lidInferenceReportIndexAtom = atomWithReset(0);
export const lidInferenceQueueAtom = atomWithReset<MLInference[]>([]);
export const lidLatencySumAtom = atomWithReset(0);
export const lidInferenceCountAtom = atomWithReset(0);

// =============================================================================
// LID — pipe & start atom
// =============================================================================

/** Ring Buffer에 축적할 오디오 길이. LID 14초 모델 입력(224,000 samples)에 맞춘다. */
const LID_SAMPLE_DURATION_MS = 14000;

/** 현재 매치의 LidAudioPipe 인스턴스. handleDisconnectAtom에서 dispose된다 */
export const lidPipeAtom = atom<LidAudioPipe | null>(null);

interface StartLidProps {
  modelConfig: BaseMLConfig;
  sourceStream: MediaStream;
  onInference: (result: LidInferenceResult) => void;
}

/**
 * LidAudioPipe를 동적 임포트 → 생성 → init 후 반환한다.
 * Antman/HVM과 달리 로컬 오디오 스트림을 입력으로 받는다.
 */
export const startLidAtom = atom(
  null,
  async (get, _set, { modelConfig, sourceStream, onInference }: StartLidProps) => {
    const entry = get(modelEntryAtoms[MLModelType.LID]);
    if (!isReadyModelEntry(entry)) {
      return null;
    }

    const { default: LidAudioPipeClass } = await import(
      /* webpackChunkName: "LIDPipe" */ 'src/utils/media/pipes/lid'
    );

    const { modelConfig: config } = modelConfig;
    const lidPipe = new LidAudioPipeClass(
      {
        intervalMs: config.intervalMs,
        maxRun: config.maxRun,
        sampleDurationMs: LID_SAMPLE_DURATION_MS,
        modelBuffer: entry.buffer,
      },
      onInference
    );

    try {
      await lidPipe.init(sourceStream);
    } catch (error) {
      lidPipe.dispose();
      throw error;
    }
    return lidPipe;
  }
);

// =============================================================================
// ML config bootstrap (Antman + HVM + LID API)
// =============================================================================

export const getMLConfigAtom = atom(null, async (get, set) => {
  const modelIdsByType = DLC_MODEL_TYPES.reduce<Partial<Record<MLModelType, string | undefined>>>(
    (acc, modelType) => {
      acc[modelType] = get(modelEntryAtoms[modelType]).modelId;
      return acc;
    },
    {}
  );

  const modelIds = DLC_MODEL_TYPES.map((modelType) => modelIdsByType[modelType]).filter(
    (id): id is string => id !== undefined
  );

  // modelId가 하나도 없으면 모델 자체가 없으므로 API 호출 스킵
  if (modelIds.length === 0) {
    return;
  }

  const clearModelConfig = (modelType: MLModelType) => {
    set(mlConfigStoreAtom, {
      ...get(mlConfigStoreAtom),
      [modelType]: undefined,
    });
  };

  const applyFallbackConfigAndModel = async (modelType: FallbackMLModelType) => {
    set(applyFallbackMLConfigAtom, modelType);
    await set(ensureFallbackModelAtom, modelType);
  };

  try {
    const { data } = await getMLModelConfigsAPI({
      modelIds,
    });

    for (const modelType of DLC_MODEL_TYPES) {
      const modelId = modelIdsByType[modelType];
      if (!modelId) continue;

      const resolvedConfig = resolveModelConfigFromAPI(modelType, modelId, data);
      if (resolvedConfig) {
        const featureConfigs =
          modelType === MLModelType.HumanVision
            ? filterHvmFeaturesByRC(resolvedConfig.featureConfigs, get(remoteConfigAtom))
            : resolvedConfig.featureConfigs;

        set(mlConfigStoreAtom, {
          ...get(mlConfigStoreAtom),
          [modelType]: {
            ...resolvedConfig,
            featureConfigs,
          },
        });
      } else {
        Sentry.captureMessage(`Failed to get ${modelType} ML configs for: ${modelId}`);
        if (isFallbackMLModelType(modelType)) {
          await applyFallbackConfigAndModel(modelType);
        } else {
          clearModelConfig(modelType);
        }
      }
    }
  } catch (error) {
    Sentry.captureMessage(`Failed to get ML configs for: ${modelIds} - ${error}`);
    await Promise.allSettled(
      DLC_MODEL_TYPES.filter(
        (modelType): modelType is FallbackMLModelType =>
          isFallbackMLModelType(modelType) && !!modelIdsByType[modelType]
      ).map(applyFallbackConfigAndModel)
    );
  }
});

// =============================================================================
// Antman — runtime (media stream)
// =============================================================================

interface StartAntmanProps {
  onInitSuccess?: () => void;
  onInitFail?: () => void;
  onResult?: (result: AntmanResultResponse) => void;
  onTrigger?: (featureType: MLFeatureType) => void;
  onSuspend?: (latency: number) => void;
}

interface CreateAntmanMediaStreamProps extends StartAntmanProps {
  modelBuffer: ArrayBuffer;
  modelId: string;
  modelConfig: MLModelConfig;
  featureConfigs: MLFeatureConfig[];
  isEnabled: () => boolean;
  onLatencyLimitExceeded: (latency: number) => void;
}

async function createAntmanMediaStream({
  modelBuffer,
  modelId,
  modelConfig,
  featureConfigs,
  isEnabled,
  onLatencyLimitExceeded,
  onInitSuccess,
  onInitFail,
  onResult,
  onTrigger,
}: CreateAntmanMediaStreamProps): Promise<AntmanMediaStreamInstance> {
  const { default: AntmanMediaStream } = await AntmanMediaStreamPromise;
  const featureTriggerStates = createModelFeatureTriggerStates(featureConfigs, MLModelType.Antman);
  const triggeredOnceFeatures = new Set<MLFeatureType>();

  const antmanMediaStream = new AntmanMediaStream(
    modelBuffer,
    modelId,
    modelConfig.intervalMs,
    (antmanResult) => {
      if (!isEnabled()) return;

      if ('isInitSuccess' in antmanResult) {
        if (antmanResult.isInitSuccess) {
          onInitSuccess?.();
        } else {
          Sentry.captureException(antmanResult.reason);
          antmanMediaStream.dispose();
          onInitFail?.();
        }
        return;
      }

      const { result, time, skipSuspend } = antmanResult;

      onResult?.(antmanResult);

      const triggeredFeatureTypes = [
        ...new Set(
          evaluateTriggeredFeatureStates(featureTriggerStates, result).map(
            ({ featureType }) => featureType
          )
        ),
      ];

      triggeredFeatureTypes.forEach((featureType) => {
        if (featureType === MLFeatureType.MatchAutoReportImage) {
          if (!triggeredOnceFeatures.has(featureType)) {
            triggeredOnceFeatures.add(featureType);
            onTrigger?.(featureType);
          }
        } else {
          onTrigger?.(featureType);
        }
      });

      if (!skipSuspend && time >= modelConfig.maxLatencyLimit) {
        onLatencyLimitExceeded(time);
      }
    }
  );

  return antmanMediaStream;
}

export const startAntmanAtom = atom(null, async (get, set, props: StartAntmanProps) => {
  if (!get(antmanEnableAtom)) return null;

  const entry = get(modelEntryAtoms[MLModelType.Antman]);
  if (!isReadyModelEntry(entry)) return null;

  const antmanConfig = get(antmanConfigAtom);
  const { modelConfig, featureConfigs } = antmanConfig;

  return createAntmanMediaStream({
    ...props,
    modelBuffer: entry.buffer,
    modelId: entry.modelId,
    modelConfig,
    featureConfigs,
    isEnabled: () => get(antmanEnableAtom),
    onLatencyLimitExceeded: (latency) => {
      // antman predict가 max latency 초과하면 turn off
      set(antmanSuspendedAtom, true);
      set(antmanEnableAtom, false);
      props.onSuspend?.(latency);
    },
  });
});

export const prepareAntmanAtom = atom(null, async (get, set) => {
  if (!get(antmanEnableAtom)) return;

  const entry = get(modelEntryAtoms[MLModelType.Antman]);
  if (entry.status === 'error') {
    set(antmanEnableAtom, false);
    return;
  }
  if (!isReadyModelEntry(entry)) {
    return;
  }

  const wasCached = entry.cached;
  const antmanMediaStream = await set(startAntmanAtom, {
    onInitSuccess: () => {
      antmanMediaStream?.dispose();
    },
    onInitFail: async () => {
      if (wasCached) {
        await set(invalidateAndRefetchModelAtom, MLModelType.Antman).catch(() => {});
      }

      // cached model evict 후 fresh download 성공 또는 원래 non-cached entry인 경우,
      // fallback config + fallback binary로 1회 재시도
      set(applyFallbackMLConfigAtom, MLModelType.Antman);
      await set(ensureFallbackModelAtom, MLModelType.Antman);

      const fallbackEntry = get(modelEntryAtoms[MLModelType.Antman]);
      if (fallbackEntry.status === 'error') {
        set(antmanEnableAtom, false);
        return;
      }
      if (!isReadyModelEntry(fallbackEntry)) {
        // 매치 시작 시 ready 상태가 되면 antman 사용 가능하도록 하기 위함
        return;
      }

      const fallbackAntmanMediaStream = await set(startAntmanAtom, {
        onInitSuccess: () => {
          fallbackAntmanMediaStream?.dispose();
        },
        onInitFail: () => {
          set(antmanEnableAtom, false);
        },
      });
    },
  });
});

// =============================================================================
// Human Vision — pipe & inference runtime
// =============================================================================

const HumanvisionMediaStreamPromise = import(
  /* webpackChunkName: "HumanVision" */ 'src/utils/media/pipes/humanvision'
);
type HumanVisionMediaStreamInstance = InstanceType<
  Awaited<typeof HumanvisionMediaStreamPromise>['default']
>;

export const humanVisionPipeAtom = atom<HumanVisionMediaStreamInstance | null>(null);

export interface HVFeatureResult extends HumanVisionInferenceResponse {
  triggeredFeatureTypes: MLFeatureType[];
}

export interface HVTriggeredFeature {
  featureType: MLFeatureType;
  score: number;
}

export interface HVTriggerBatch {
  result: number[];
  triggeredFeatures: HVTriggeredFeature[]; // featureType unique
}

export function createHumanVisionTriggerBatch(
  triggeredFeatureStates: FeatureTriggerState[],
  result: number[]
): HVTriggerBatch | null {
  // feature trigger states에 여러 condition이 있을 수 있으므로, 같은 feature의 max score를 계산
  const scoreByFeatureType = new Map<MLFeatureType, number>();

  triggeredFeatureStates.forEach(({ featureType, condition }) => {
    const score = result[condition.modelIndex] ?? 0;
    const previousScore = scoreByFeatureType.get(featureType);
    scoreByFeatureType.set(
      featureType,
      previousScore == null ? score : Math.max(previousScore, score)
    );
  });

  if (scoreByFeatureType.size === 0) {
    return null;
  }

  return {
    result,
    triggeredFeatures: Array.from(scoreByFeatureType, ([featureType, score]) => ({
      featureType,
      score,
    })),
  };
}

interface StartHumanVisionProps {
  onInitSuccess?: () => void;
  onInitFail?: () => void;
  onResult?: (result: HVFeatureResult) => void;
  onTrigger?: (batch: HVTriggerBatch) => void;
  onSuspend?: (latency: number) => void;
}

interface CreateHumanVisionMediaStreamProps
  extends Pick<StartHumanVisionProps, 'onInitSuccess' | 'onInitFail' | 'onResult' | 'onTrigger'> {
  modelBuffer: ArrayBuffer;
  modelId: string;
  modelConfig: MLModelConfig;
  featureConfigs: MLFeatureConfig[];
  onLatencyLimitExceeded: (latency: number) => void;
}

async function createHumanVisionMediaStream({
  modelBuffer,
  modelId,
  modelConfig,
  featureConfigs,
  onLatencyLimitExceeded,
  onInitSuccess,
  onInitFail,
  onResult,
  onTrigger,
}: CreateHumanVisionMediaStreamProps): Promise<HumanVisionMediaStreamInstance> {
  const { default: HumanvisionMediaStream } = await HumanvisionMediaStreamPromise;
  const featureTriggerStates = createModelFeatureTriggerStates(
    featureConfigs,
    MLModelType.HumanVision
  );

  const humanVisionMediaStream = new HumanvisionMediaStream(
    modelBuffer,
    modelId,
    modelConfig,
    (humanVisionResult) => {
      if ('isInitSuccess' in humanVisionResult) {
        if (humanVisionResult.isInitSuccess) {
          onInitSuccess?.();
        } else {
          Sentry.captureException(humanVisionResult.reason);
          humanVisionMediaStream.dispose();
          onInitFail?.();
        }
        return;
      }

      // feature별 독립 ring buffer 기반 판정 (Android checkAbuse와 동일: 매 추론마다 평가)
      const triggeredFeatureStates = evaluateTriggeredFeatureStates(
        featureTriggerStates,
        humanVisionResult.result
      );
      const triggerBatch = createHumanVisionTriggerBatch(
        triggeredFeatureStates,
        humanVisionResult.result
      );
      if (triggerBatch) {
        onTrigger?.(triggerBatch);
      }

      onResult?.({
        ...humanVisionResult,
        triggeredFeatureTypes:
          triggerBatch?.triggeredFeatures.map(({ featureType }) => featureType) ?? [],
      });

      // hvm predict가 max latency 초과하면 suspend
      if (humanVisionResult.time >= modelConfig.maxLatencyLimit) {
        onLatencyLimitExceeded(humanVisionResult.time);
      }
    }
  );

  return humanVisionMediaStream;
}

export const resetHumanVisionStateAtom = atom(null, (_get, set) => {
  set(hvmFeaturesCapturedAtom, RESET);
});

export const startHumanVisionAtom = atom(null, async (get, set, props: StartHumanVisionProps) => {
  const remoteConfig = get(remoteConfigAtom);
  if (!isHumanVisionEnabled(remoteConfig)) return null;

  const entry = get(modelEntryAtoms[MLModelType.HumanVision]);
  if (!isReadyModelEntry(entry)) return null;

  const { modelConfig } = get(hvmModelConfigAtom);

  // HumanVision feature config를 기반으로 stream-local trigger state를 만든다.
  // getMLConfigAtom의 async fetch가 완료되기 전에 매치가 시작될 수 있으므로,
  // hvmFeatureConfigsAtom 값과 무관하게 RC를 재검사하여 ungated fallback이 그대로 사용되는 race를 방지.
  const featureConfigs = filterHvmFeaturesByRC(get(hvmFeatureConfigsAtom), remoteConfig);

  return createHumanVisionMediaStream({
    ...props,
    modelBuffer: entry.buffer,
    modelId: entry.modelId,
    modelConfig,
    featureConfigs,
    onLatencyLimitExceeded: (latency) => {
      set(hvmSuspendedAtom, true);
      props.onSuspend?.(latency);
    },
  });
});
